import React, { useEffect, useState, useRef, useMemo } from 'react'
import Card from './Card.tsx'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink.tsx'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale.ts'
import { IEcommerceQuestionSchema, EcommerceResponse } from '@aytm/app/swidget/base/questions/Ecommerce/types'
import PromoRow from './PromoRow.tsx'
import { countDrawnCards } from '../../utils/hiddenClass.ts'
import style from './Main.module.scss'

interface IMainProps {
  schema: IEcommerceQuestionSchema
  response: EcommerceResponse
  deliveryDates: string[]
  findability: boolean
  onAddToCart: (qnt: number, index: number, dropdown: boolean) => void
  onItemSelect: (index: number, bannerPlacement?: string) => void
  onSidebarShow: (index: number) => void
  registerCardRef: (id: string, element: HTMLDivElement | null) => void
  resetCenterElementRef: () => void
}

const Main = ({
  schema,
  response,
  deliveryDates,
  findability,
  onAddToCart,
  onItemSelect,
  onSidebarShow,
  registerCardRef,
  resetCenterElementRef
}: IMainProps) => {
  const viewport = useViewport()
  const { t } = useSurveyLocale()
  const sponsoredSize = schema.answers.filter(answer => answer.featured).length
  const promoSize = schema.answers.filter(answer => answer.banner).length
  const containerRef = useRef<HTMLDivElement>(null)

  // Calculate where to insert banner
  const productsPerRow = schema.products_per_row || 4
  const bannerPosition = viewport.mobile ? 4 : productsPerRow * 2

  // Filter regular products (not featured, not banner, not na)
  const regularProducts = useMemo(
    () => schema.answers.filter(answer => !answer.featured && !answer.banner && !answer.na && !answer.parent_product),
    [schema.answers]
  )

  // Split products: before and after banner
  const productsBeforeBanner = regularProducts.slice(0, bannerPosition)
  const productsAfterBanner = regularProducts.slice(bannerPosition)
  const [currentIndex, setCurrentIndex] = useState(0)
  const scrollTo = (index: number) => {
    setCurrentIndex(index)
    if (containerRef.current) {
      const scrollLeft = (index * containerRef.current.clientWidth) / 6
      containerRef.current.scrollTo({ left: scrollLeft })
    }
  }
  useEffect(() => {
    if (containerRef.current && sponsoredSize && !viewport.mobile) {
      containerRef.current.addEventListener(
        'wheel',
        event => {
          if (event.deltaX != 0) {
            event.stopPropagation()
            event.preventDefault()
          }
        },
        { passive: false }
      )
    }
  }, [sponsoredSize, viewport.mobile])
  return (
    <>
      <div className={`${style.main} ${viewport.mobile ? style.mainMobile : ''}`}>
        <div className={style.topFilters}>
          {viewport.mobile ? (
            <div className={style.filtersInner}>
              <EmptyLink color="#CC0000">
                <div className={style.filterItem}>
                  <div className={style.filterIcon} />
                  <div className={style.filterText}>{t('questions', 'ec', 'target', 'filter')}</div>
                </div>
              </EmptyLink>
              <EmptyLink color="#CC0000">
                <div className={style.filterItem}>
                  <div className={style.sortIcon} />
                  <div className={style.filterText}>{t('questions', 'ec', 'target', 'sort')}</div>
                </div>
              </EmptyLink>
              <EmptyLink color="#CC0000">
                <div className={style.filterItem}>
                  <div className={style.driveUpIcon} />
                  <div className={style.filterText}>{t('questions', 'ec', 'target', 'pickup')}</div>
                </div>
              </EmptyLink>
              <EmptyLink color="#CC0000">
                <div className={style.filterItem}>
                  <div className={style.sameDayIcon} />
                  <div className={style.filterText}>{t('questions', 'ec', 'target', 'delivery')}</div>
                </div>
              </EmptyLink>
              <EmptyLink color="#CC0000">
                <div className={style.filterItem}>
                  <div className={style.shippingIcon} />
                  <div className={style.filterText}>{t('questions', 'ec', 'target', 'shipping')}</div>
                </div>
              </EmptyLink>
            </div>
          ) : (
            <div className={style.filtersInner}>
              <EmptyLink color="#CC0000">
                <div className={style.filterItem}>
                  <div className={style.filterIcon} />
                  <div className={style.filterText}>{t('questions', 'ec', 'target', 'filter')}</div>
                </div>
              </EmptyLink>
              <EmptyLink color="#CC0000">
                <div className={style.filterItem}>
                  <div className={style.sortIcon} />
                  <div className={style.filterText}>{t('questions', 'ec', 'target', 'sort')}</div>
                </div>
              </EmptyLink>
              <EmptyLink color="#CC0000">
                <div className={style.filterItem}>
                  <div className={style.dealsIcon} />
                  <div className={style.filterText}>{t('questions', 'ec', 'target', 'circleDeals')}</div>
                </div>
              </EmptyLink>
              <EmptyLink color="#CC0000">
                <div className={style.filterItem}>
                  <div className={style.filterText}>{t('questions', 'ec', 'target', 'deals')}</div>
                </div>
              </EmptyLink>
              <EmptyLink color="#CC0000">
                <div className={style.filterItem}>
                  <div className={style.filterText}>{t('questions', 'ec', 'target', 'brand')}</div>
                </div>
              </EmptyLink>
              <EmptyLink color="#CC0000">
                <div className={style.filterItem}>
                  <div className={style.filterText}>{t('questions', 'ec', 'target', 'type')}</div>
                </div>
              </EmptyLink>
              <EmptyLink color="#CC0000">
                <div className={style.filterItem}>
                  <div className={style.filterText}>{t('questions', 'ec', 'target', 'guestRating')}</div>
                </div>
              </EmptyLink>
              <EmptyLink color="#CC0000">
                <div className={style.filterItem}>
                  <div className={style.filterText}>{t('questions', 'ec', 'target', 'activity')}</div>
                </div>
              </EmptyLink>
              <EmptyLink color="#CC0000">
                <div className={style.filterItem}>
                  <div className={style.filterText}>{t('questions', 'ec', 'target', 'soldBy')}</div>
                </div>
              </EmptyLink>
              <EmptyLink color="#CC0000">
                <div className={style.filterItem}>
                  <div className={style.filterText}>{t('questions', 'ec', 'target', 'price')}</div>
                </div>
              </EmptyLink>
              <EmptyLink color="#CC0000">
                <div className={style.filterItem}>
                  <div className={style.pickupIcon} />
                  <div className={style.filterText}>{t('questions', 'ec', 'target', 'pickup')}</div>
                </div>
              </EmptyLink>
              <EmptyLink color="#CC0000">
                <div className={style.filterItem}>
                  <div className={style.shopIcon} />
                  <div className={style.filterText}>{t('questions', 'ec', 'target', 'shopIn')}</div>
                </div>
              </EmptyLink>
              <EmptyLink color="#CC0000">
                <div className={style.filterItem}>
                  <div className={style.sameDayIcon} />
                  <div className={style.filterText}>{t('questions', 'ec', 'target', 'sameDay')}</div>
                </div>
              </EmptyLink>
              <EmptyLink color="#CC0000">
                <div className={style.filterItem}>
                  <div className={style.shippingIcon} />
                  <div className={style.filterText}>{t('questions', 'ec', 'target', 'shipping')}</div>
                </div>
              </EmptyLink>
            </div>
          )}
        </div>
        <div className={style.head}>
          {schema.search_condition && (
            <div className={style.result}>
              <span className={style.count}>{`${countDrawnCards(regularProducts)} ${t(
                'questions',
                'ec',
                'target',
                'results'
              )}`}</span>
              <span className={style.light}>
                &nbsp; {t('questions', 'ec', 'target', 'for')} &#34;{schema.search_condition}&#34;
              </span>
            </div>
          )}
        </div>
        <div className={style.products}>
          {sponsoredSize > 0 && (
            <div className={style.buttonsContainer} onClick={resetCenterElementRef}>
              <div
                className={`${style.sponsoredTitle} ${schema.search_condition ? '' : style.firstLetterUppercase}`}
                dangerouslySetInnerHTML={{
                  __html: `${schema.search_condition || ''} ${t('questions', 'ec', 'target', 'dealsForYou')}`
                }}
              />
              {currentIndex > 0 && !viewport.mobile ? (
                <div
                  className={style.left}
                  onClick={() => {
                    scrollTo(currentIndex - 6)
                  }}
                >
                  <i className="pi pi-chevron-left"></i>
                </div>
              ) : (
                ''
              )}
              <div className={` ${style.carouselContainer}`} ref={containerRef}>
                <div className={` ${style.carousel}`}>
                  {schema.answers
                    .filter(answer => answer.featured)
                    .filter(answer => !answer.na)
                    .map((answer, index) => (
                      <Card
                        key={index}
                        data-testid="SW-SH-Item"
                        answer={answer}
                        response={response.answers[answer.index]}
                        schema={schema}
                        index={answer.index}
                        findability={findability}
                        small={true}
                        disabled={'focus' == schema.mode_type && !answer.focused}
                        zIndex={schema.answers.length + 10 - answer.index}
                        showDropdown={false}
                        bannerPlacement="featured"
                        onAddToCart={onAddToCart}
                        onItemSelect={onItemSelect}
                        deliveryDate={deliveryDates[answer.index]}
                        onSidebarShow={onSidebarShow}
                      />
                    ))}
                </div>
              </div>
              {sponsoredSize - 6 > currentIndex && !viewport.mobile ? (
                <div
                  className={style.right}
                  onClick={() => {
                    scrollTo(currentIndex + 6)
                  }}
                >
                  <i className="pi pi-chevron-right"></i>
                </div>
              ) : (
                ''
              )}
            </div>
          )}
          {/* Products before banner */}
          {productsBeforeBanner.map(answer => (
            <Card
              key={answer.id}
              ref={element => registerCardRef(answer.id, element)}
              data-testid="SW-SH-Item"
              answer={answer}
              response={response.answers[answer.index]}
              schema={schema}
              index={answer.index}
              findability={findability}
              disabled={'focus' == schema.mode_type && !answer.focused}
              zIndex={schema.answers.length + 10 - answer.index}
              showDropdown={true}
              applyCustomWidth={true}
              onAddToCart={onAddToCart}
              onItemSelect={onItemSelect}
              deliveryDate={deliveryDates[answer.index]}
              onSidebarShow={onSidebarShow}
            />
          ))}

          {/* Promo Banner (if exists and we have products after banner) */}
          {promoSize > 0 && productsAfterBanner.length > 0 && <PromoRow schema={schema} onItemSelect={onItemSelect} />}

          {/* Products after banner */}
          {productsAfterBanner.map(answer => (
            <Card
              key={answer.id}
              ref={element => registerCardRef(answer.id, element)}
              data-testid="SW-SH-Item"
              answer={answer}
              response={response.answers[answer.index]}
              schema={schema}
              index={answer.index}
              findability={findability}
              disabled={'focus' == schema.mode_type && !answer.focused}
              zIndex={schema.answers.length + 10 - answer.index}
              showDropdown={true}
              applyCustomWidth={true}
              onAddToCart={onAddToCart}
              onItemSelect={onItemSelect}
              deliveryDate={deliveryDates[answer.index]}
              onSidebarShow={onSidebarShow}
            />
          ))}
        </div>
      </div>
    </>
  )
}

export default Main

Main.displayName = 'Main'
